import {
  Body,
  Button,
  Flex,
  Grid,
  Heading,
  Stack,
  useToast,
} from '@hover/blueprint';
import type { ApolloError } from 'apollo-server-express';
import type { GraphQLError } from 'graphql';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { makeInviteValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Form as SharedForm, TextField } from 'src/shared/components/forms';
import { useOnboardingCreateOrgUsersMutation } from 'src/shared/graphql/generated';
import { useEffectOnMount, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

import { useOnboardingError } from '../hooks/useOnboardingError';

export type UserInviteFormData = InferType<
  ReturnType<typeof makeInviteValidationSchema>
>;

// Strictly defined 3 values on current design. Turning this into an array creates
// complication on the form side for displaying errors, so keeping it straightforward
// unless programmatically defined fields are required.
interface InviteFormData {
  firstEmail?: string;
  secondEmail?: string;
  thirdEmail?: string;
}

const UserInviteFormFields = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const isMobile = useIsMobileSize();

  return (
    <Stack
      alignItems="stretch"
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="nowrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      paddingX={{ base: 0, tablet: '10%' }}
      spacing={0}
      width="auto"
    >
      <Flex flexDir="column" maxW={800}>
        <Heading
          mb={{ base: 400, tablet: 200 }}
          size={{ base: 700, tablet: 800 }}
        >
          {t('onboarding.inviteYourTeam')}
        </Heading>
        {!isMobile && (
          <Body color="neutral.600" mt={0}>
            {t('onboarding.inviteUnlimitedMembers')}
          </Body>
        )}
      </Flex>
      <Flex alignItems="center" flexBasis="45%" flexDir="column">
        <TextField
          name="firstEmail"
          placeholder={t('onboarding.teammateEmail')}
          type="email"
          width={1}
        />
        <TextField
          name="secondEmail"
          placeholder={t('onboarding.teammateEmail')}
          type="email"
          width={1}
        />
        <TextField
          name="thirdEmail"
          placeholder={t('onboarding.teammateEmail')}
          type="email"
          width={1}
        />
      </Flex>
    </Stack>
  );
};

const UserInviteFormContent = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch } = useFormContext();

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-teaminvite',
      feature: 'onboarding',
    });
  });

  const watchEmails = watch(['firstEmail', 'secondEmail', 'thirdEmail']);
  const hasEmails = useMemo(
    () =>
      [
        watchEmails.firstEmail,
        watchEmails.secondEmail,
        watchEmails.thirdEmail,
      ].some(element => !!element),
    [watchEmails],
  );

  const track = () => {
    buttonPressed({
      feature: 'onboarding',
      page_or_screen_name: 'Onboarding-teaminvite',
      button_text: hasEmails
        ? t('onboarding.sendInvites')
        : t('onboarding.continue'),
    });
  };

  return (
    <>
      <UserInviteFormFields />
      <Flex flexDir="row" justifyContent="end" width={1}>
        <Button
          onClick={track}
          type="submit"
          variant="primary"
          width={{ base: 1, tablet: 'auto' }}
        >
          {hasEmails ? t('onboarding.sendInvites') : t('onboarding.continue')}
        </Button>
      </Flex>
    </>
  );
};

export const UserInvite = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { logError } = useOnboardingError();
  const validationSchema = makeInviteValidationSchema(t);
  const [createOrgUsers] = useOnboardingCreateOrgUsersMutation();
  const toast = useToast();

  const navigateToPhone = () => {
    history.push(`${Routes.SIGN_UP_ONBOARDING.path}/phone`);
  };

  const onSubmit = async (formData: InviteFormData) => {
    const { firstEmail, secondEmail, thirdEmail } = formData;

    const users = (
      [firstEmail, secondEmail, thirdEmail].filter(
        Boolean,
      ) as NonNullable<string>[]
    ).map(email => ({
      email,
    }));

    if (users.length > 0) {
      try {
        await createOrgUsers({
          variables: {
            createOrgUsersArgs: {
              users,
            },
          },
        });
      } catch (e) {
        const error = e as ApolloError;
        const graphQLErrors = error.graphQLErrors as GraphQLError[];
        if (graphQLErrors) {
          const errorString = graphQLErrors?.reduce(
            (acc: string, current) =>
              `${acc}${String(current.extensions?.email)}: ${
                current.message
              }\n`,
            '',
          );
          toast({
            title: t('onboarding.errorInvitingTeam'),
            description: errorString,
            status: 'error',
            duration: 5000,
            isClosable: true,
            containerStyle: {
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
            },
          });
        } else {
          logError(e as Error);
        }
      } finally {
        navigateToPhone();
      }
    } else {
      navigateToPhone();
    }
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserInviteFormData>
        display="flex"
        height="100%"
        initialValues={{}}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UserInviteFormContent />
      </SharedForm>
    </Grid>
  );
};
